import React, { useState, useEffect, useCallback } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Truck,
  Package,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Phone,
  Navigation,
  Play,
  DollarSign,
  Camera,
  MessageCircle,
  ChevronRight,
  User,
  RefreshCw,
  Map,
} from "lucide-react";

const DELIVERY_STATUS_CONFIG = {
  pending_dispatch: { label: "Pendiente", color: "bg-gray-500", textColor: "text-gray-700" },
  assigned: { label: "Asignado", color: "bg-blue-500", textColor: "text-blue-700" },
  on_route: { label: "En Ruta", color: "bg-yellow-500", textColor: "text-yellow-700" },
  delivered: { label: "Entregado", color: "bg-green-600", textColor: "text-green-700" },
  failed_delivery: { label: "No Entregado", color: "bg-red-500", textColor: "text-red-700" },
  rescheduled: { label: "Reprogramado", color: "bg-orange-500", textColor: "text-orange-700" },
};

const ISSUE_TYPES = [
  { value: "cliente_no_responde", label: "Cliente no responde" },
  { value: "direccion_incorrecta", label: "Dirección incorrecta" },
  { value: "cliente_ausente", label: "Cliente no estaba" },
  { value: "cliente_reprograma", label: "Cliente quiere reprogramar" },
  { value: "no_pago", label: "Cliente no pagó" },
  { value: "producto_incompleto", label: "Producto incompleto" },
  { value: "problema_acceso", label: "Problema de acceso" },
  { value: "otro", label: "Otro" },
];

export default function DeliveryDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Dialog states
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDeliverDialog, setShowDeliverDialog] = useState(false);
  const [showIssueDialog, setShowIssueDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Deliver form
  const [deliverForm, setDeliverForm] = useState({
    cash_collected: "",
    receiver_name: "",
    notes: "",
  });
  
  // Issue form
  const [issueForm, setIssueForm] = useState({
    issue_type: "",
    description: "",
    reschedule_date: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get("/despachos/domiciliario/mi-dashboard");
      setData(res.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      if (error.response?.status === 403) {
        toast.error("No tienes permiso para acceder a entregas");
      } else {
        toast.error("Error al cargar datos");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh cada 2 minutos
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleStartRoute = async () => {
    try {
      await api.post("/despachos/mi-ruta/iniciar");
      toast.success("¡Ruta iniciada! Buen viaje");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar ruta");
    }
  };

  const openDeliverDialog = (order) => {
    setSelectedOrder(order);
    setDeliverForm({
      cash_collected: order.requiere_cobro ? String(order.total) : "",
      receiver_name: "",
      notes: "",
    });
    setShowDeliverDialog(true);
  };

  const openIssueDialog = (order) => {
    setSelectedOrder(order);
    setIssueForm({
      issue_type: "",
      description: "",
      reschedule_date: "",
    });
    setShowIssueDialog(true);
  };

  const handleDeliver = async () => {
    if (!selectedOrder) return;
    
    // Validación para pagos contra entrega
    if (selectedOrder.requiere_cobro && !deliverForm.cash_collected) {
      toast.error("Debes ingresar el valor cobrado");
      return;
    }
    
    setActionLoading(true);
    try {
      const params = new URLSearchParams();
      if (deliverForm.cash_collected) {
        params.append("cash_collected", deliverForm.cash_collected);
      }
      if (deliverForm.receiver_name) {
        params.append("receiver_name", deliverForm.receiver_name);
      }
      if (deliverForm.notes) {
        params.append("notes", deliverForm.notes);
      }
      
      await api.post(`/despachos/domiciliario/pedido/${selectedOrder.id}/entregar?${params.toString()}`);
      toast.success("¡Pedido entregado!");
      setShowDeliverDialog(false);
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al marcar como entregado");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReportIssue = async () => {
    if (!selectedOrder || !issueForm.issue_type) {
      toast.error("Selecciona el tipo de novedad");
      return;
    }
    
    setActionLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("issue_type", issueForm.issue_type);
      if (issueForm.description) {
        params.append("description", issueForm.description);
      }
      if (issueForm.reschedule_date) {
        params.append("reschedule_date", issueForm.reschedule_date);
      }
      
      await api.post(`/despachos/domiciliario/pedido/${selectedOrder.id}/novedad?${params.toString()}`);
      toast.success("Novedad reportada");
      setShowIssueDialog(false);
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al reportar novedad");
    } finally {
      setActionLoading(false);
    }
  };

  const openWhatsApp = (phone, nombre) => {
    const cleanPhone = phone?.replace(/\D/g, "");
    const message = encodeURIComponent(`Hola ${nombre}, soy de Baruj Shefa. Voy en camino con tu pedido.`);
    window.open(`https://wa.me/57${cleanPhone}?text=${message}`, "_blank");
  };

  const openMaps = (direccion, barrio) => {
    const query = encodeURIComponent(`${direccion}, ${barrio}, Bogotá, Colombia`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C8902A]"></div>
      </div>
    );
  }

  const dispatch = data?.dispatch;
  const orders = data?.orders || [];
  const stats = data?.stats || {};

  // Separar pedidos por estado
  const pendingOrders = orders.filter(o => 
    !["delivered", "failed_delivery", "rescheduled", "cancelled_dispatch"].includes(o.delivery_status)
  );
  const completedOrders = orders.filter(o => 
    ["delivered", "failed_delivery", "rescheduled"].includes(o.delivery_status)
  );

  return (
    <div className="p-4 space-y-4" data-testid="delivery-dashboard">
      {/* Header con Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Playfair_Display'] text-xl text-[#4A2C0A] font-bold">
            Mi Ruta de Hoy
          </h1>
          <p className="text-sm text-[#4A2C0A]/60">{data?.fecha}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-2">
        <div className="bg-white rounded-xl p-3 border border-[#E5D4B3] text-center">
          <p className="text-xl font-bold text-[#4A2C0A]">{stats.total}</p>
          <p className="text-[10px] text-[#4A2C0A]/60">Total</p>
        </div>
        <div className="bg-green-50 rounded-xl p-3 border border-green-200 text-center">
          <p className="text-xl font-bold text-green-600">{stats.delivered}</p>
          <p className="text-[10px] text-green-600">Entregados</p>
        </div>
        <div className="bg-yellow-50 rounded-xl p-3 border border-yellow-200 text-center">
          <p className="text-xl font-bold text-yellow-600">{stats.pending}</p>
          <p className="text-[10px] text-yellow-600">Pendientes</p>
        </div>
        <div className="bg-[#C8902A]/10 rounded-xl p-3 border border-[#C8902A]/30 text-center">
          <p className="text-xl font-bold text-[#C8902A]">
            ${(stats.total_to_collect || 0).toLocaleString("es-CO")}
          </p>
          <p className="text-[10px] text-[#C8902A]">Por Cobrar</p>
        </div>
      </div>

      {/* Estado de la Ruta */}
      {dispatch ? (
        <div className="bg-white rounded-xl p-4 border border-[#E5D4B3]">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-[#4A2C0A]">{dispatch.name}</h2>
              <p className="text-sm text-[#4A2C0A]/60">
                {dispatch.zone && `Zona: ${dispatch.zone}`}
                {dispatch.time_window && ` • ${dispatch.time_window}`}
              </p>
            </div>
            {dispatch.status === "assigned" && (
              <Button
                onClick={handleStartRoute}
                className="gap-2 bg-green-600 hover:bg-green-700"
              >
                <Play className="w-4 h-4" />
                Iniciar Ruta
              </Button>
            )}
            {dispatch.status === "in_progress" && (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                En Progreso
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl p-8 border border-[#E5D4B3] text-center">
          <Truck className="w-12 h-12 mx-auto mb-3 text-[#E5D4B3]" />
          <p className="text-[#4A2C0A] font-medium">No tienes ruta asignada hoy</p>
          <p className="text-sm text-[#4A2C0A]/60 mt-1">
            Contacta a central si crees que esto es un error
          </p>
        </div>
      )}

      {/* Pedidos Pendientes */}
      {pendingOrders.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-semibold text-[#4A2C0A] flex items-center gap-2">
            <Clock className="w-4 h-4 text-yellow-600" />
            Pendientes ({pendingOrders.length})
          </h2>
          
          {pendingOrders.map((order, idx) => (
            <OrderCard
              key={order.id}
              order={order}
              index={idx + 1}
              onDeliver={() => openDeliverDialog(order)}
              onIssue={() => openIssueDialog(order)}
              onWhatsApp={() => openWhatsApp(order.cliente_telefono, order.cliente_nombre)}
              onMaps={() => openMaps(order.direccion_entrega, order.barrio_entrega)}
            />
          ))}
        </div>
      )}

      {/* Pedidos Completados */}
      {completedOrders.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-semibold text-[#4A2C0A] flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            Completados ({completedOrders.length})
          </h2>
          
          {completedOrders.map((order, idx) => (
            <CompletedOrderCard key={order.id} order={order} />
          ))}
        </div>
      )}

      {/* Dialog: Marcar Entregado */}
      <Dialog open={showDeliverDialog} onOpenChange={setShowDeliverDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#4A2C0A] flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Confirmar Entrega
            </DialogTitle>
            <DialogDescription>
              Pedido: <strong>{selectedOrder?.numero_pedido}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Monto a cobrar */}
            {selectedOrder?.requiere_cobro && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <Label className="text-orange-700 font-medium flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Valor Cobrado (Obligatorio)
                </Label>
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-orange-600 font-bold">$</span>
                  <Input
                    type="number"
                    value={deliverForm.cash_collected}
                    onChange={(e) => setDeliverForm({ ...deliverForm, cash_collected: e.target.value })}
                    className="pl-8 text-lg font-bold border-orange-300 focus:border-orange-500"
                    placeholder={String(selectedOrder?.total || 0)}
                  />
                </div>
                <p className="text-xs text-orange-600 mt-1">
                  Total del pedido: ${selectedOrder?.total?.toLocaleString("es-CO")}
                </p>
              </div>
            )}

            {/* Quien recibe */}
            <div>
              <Label className="text-[#4A2C0A]">¿Quién recibió? (Opcional)</Label>
              <Input
                value={deliverForm.receiver_name}
                onChange={(e) => setDeliverForm({ ...deliverForm, receiver_name: e.target.value })}
                placeholder="Nombre de quien recibió"
                className="mt-1"
              />
            </div>

            {/* Notas */}
            <div>
              <Label className="text-[#4A2C0A]">Observaciones (Opcional)</Label>
              <Textarea
                value={deliverForm.notes}
                onChange={(e) => setDeliverForm({ ...deliverForm, notes: e.target.value })}
                placeholder="Cualquier observación..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeliverDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDeliver}
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {actionLoading ? "Guardando..." : "Confirmar Entrega"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Reportar Novedad */}
      <Dialog open={showIssueDialog} onOpenChange={setShowIssueDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#4A2C0A] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              Reportar Novedad
            </DialogTitle>
            <DialogDescription>
              Pedido: <strong>{selectedOrder?.numero_pedido}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Tipo de novedad */}
            <div>
              <Label className="text-[#4A2C0A]">Tipo de Novedad *</Label>
              <Select
                value={issueForm.issue_type}
                onValueChange={(value) => setIssueForm({ ...issueForm, issue_type: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {ISSUE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Fecha sugerida si es reprogramar */}
            {issueForm.issue_type === "cliente_reprograma" && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <Label className="text-blue-700">Nueva Fecha Sugerida</Label>
                <Input
                  type="date"
                  value={issueForm.reschedule_date}
                  onChange={(e) => setIssueForm({ ...issueForm, reschedule_date: e.target.value })}
                  min={new Date().toISOString().split("T")[0]}
                  className="mt-1 border-blue-300"
                />
                <p className="text-xs text-blue-600 mt-1">
                  Central confirmará la nueva fecha
                </p>
              </div>
            )}

            {/* Descripción */}
            <div>
              <Label className="text-[#4A2C0A]">Descripción</Label>
              <Textarea
                value={issueForm.description}
                onChange={(e) => setIssueForm({ ...issueForm, description: e.target.value })}
                placeholder="Describe lo que pasó..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIssueDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleReportIssue}
              disabled={actionLoading || !issueForm.issue_type}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {actionLoading ? "Guardando..." : "Reportar Novedad"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Componente de tarjeta de pedido pendiente
function OrderCard({ order, index, onDeliver, onIssue, onWhatsApp, onMaps }) {
  return (
    <div className="bg-white rounded-xl border-2 border-[#E5D4B3] overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-[#FBF5E8] flex items-center justify-between border-b border-[#E5D4B3]">
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 bg-[#C8902A] rounded-full flex items-center justify-center text-white text-sm font-bold">
            {index}
          </span>
          <div>
            <p className="font-semibold text-[#4A2C0A]">{order.numero_pedido}</p>
            <p className="text-xs text-[#4A2C0A]/60">{order.cliente_nombre}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-bold text-[#C8902A]">${order.total?.toLocaleString("es-CO")}</p>
          {order.requiere_cobro && (
            <span className="text-[10px] px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">
              Cobrar
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Dirección */}
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-[#4A2C0A]/40 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#4A2C0A]">{order.direccion_entrega}</p>
            {order.barrio_entrega && (
              <p className="text-xs text-[#4A2C0A]/60">{order.barrio_entrega}</p>
            )}
          </div>
        </div>

        {/* Notas */}
        {order.notas_entrega && (
          <div className="bg-yellow-50 rounded-lg p-2 text-xs text-yellow-800">
            <strong>Nota:</strong> {order.notas_entrega}
          </div>
        )}

        {/* Productos */}
        <div className="text-xs text-[#4A2C0A]/60">
          {order.items?.map((item, i) => (
            <span key={i}>
              {item.cantidad}x {item.nombre}
              {i < order.items.length - 1 && " • "}
            </span>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onWhatsApp}
            className="flex-1 gap-1 border-green-500 text-green-600 hover:bg-green-50"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onMaps}
            className="flex-1 gap-1 border-blue-500 text-blue-600 hover:bg-blue-50"
          >
            <Navigation className="w-4 h-4" />
            Mapa
          </Button>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={onDeliver}
            className="flex-1 gap-1 bg-green-600 hover:bg-green-700"
          >
            <CheckCircle className="w-4 h-4" />
            Entregado
          </Button>
          <Button
            variant="outline"
            onClick={onIssue}
            className="flex-1 gap-1 border-orange-500 text-orange-600 hover:bg-orange-50"
          >
            <AlertTriangle className="w-4 h-4" />
            Novedad
          </Button>
        </div>
      </div>
    </div>
  );
}

// Componente de tarjeta de pedido completado
function CompletedOrderCard({ order }) {
  const status = DELIVERY_STATUS_CONFIG[order.delivery_status] || DELIVERY_STATUS_CONFIG.delivered;
  
  return (
    <div className="bg-white rounded-xl border border-[#E5D4B3] p-4 opacity-75">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {order.delivery_status === "delivered" ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
          <div>
            <p className="font-medium text-[#4A2C0A]">{order.numero_pedido}</p>
            <p className="text-xs text-[#4A2C0A]/60">{order.cliente_nombre}</p>
          </div>
        </div>
        <div className="text-right">
          <span className={`text-xs px-2 py-1 rounded-full ${status.color} text-white`}>
            {status.label}
          </span>
          {order.cash_collected_amount !== null && order.cash_collected_amount !== undefined && (
            <p className="text-xs text-green-600 mt-1">
              Cobrado: ${order.cash_collected_amount?.toLocaleString("es-CO")}
            </p>
          )}
        </div>
      </div>
      {order.delivery_issue_type && (
        <p className="text-xs text-red-600 mt-2">
          Novedad: {ISSUE_TYPES.find(t => t.value === order.delivery_issue_type)?.label || order.delivery_issue_type}
        </p>
      )}
    </div>
  );
}
